import {
  MagtekStatusDevice,
  MethodMagTek,
  TMagtekDevice,
  TransactionMagTek,
  TResponseMagtekDevice,
  TResponseTransaction,
  TTransactionCardData,
} from '@/types/payment/charge.type';
import { GetCardData, Platform } from 'gap-miniapp-sdk';
import { createContext, useContext, useEffect, useState } from 'react';
import { GapSdkContext } from './gap-sdk-provider';

type TGapMagtekSdkContext = {
  deviceInfo: TMagtekDevice | undefined;
  paymentStatus: string | undefined;
  event: {
    onCancel: () => void;
    onCheckStatusDevice: () => Promise<number> | undefined;
    onInitPaymentCard: () => Promise<string | JSON | null> | undefined;
  };
};

const IgnoreShowText = ['CARD READ OK, REMOVE CARD', 'REMOVE CARD'];

export const GapMagtekSdkContext = createContext<TGapMagtekSdkContext>({
  deviceInfo: undefined,
  paymentStatus: undefined,
  event: {
    onCancel: () => undefined,
    onCheckStatusDevice: () => undefined,
    onInitPaymentCard: () => undefined,
  },
});

export default function GapMagtekSdkProvider(props: {
  children?: React.ReactNode;
}) {
  const { gapMiniAppSdk } = useContext(GapSdkContext);
  const [deviceInfo, setDeviceInfo] = useState<TMagtekDevice>();
  const [paymentStatus, setPaymentStatus] = useState<string>();

  let userAgent: Platform | undefined;
  // Connect magtek, get device info & SN:
  const inputCardData: GetCardData = {
    type: 'MAGTEK',
    method: MethodMagTek.NATIVE_DEVICE_CONNECTION_STATE,
  };

  const getTransactionStatusHandler = (data: any) => {
    if (!data) return;
    const convert = JSON.parse(
      data,
    ) as TResponseTransaction<TTransactionCardData>;

    if (convert.type === TransactionMagTek.NATIVE_PAYMENT_NOTIFICATION) {
      if (!IgnoreShowText.includes(convert.data.message)) {
        setPaymentStatus(convert.data.message);
      }

      if (!convert.data.message || convert.data.message.length === 0) {
        setPaymentStatus(undefined);
      }
    }
    // eslint-disable-next-line consistent-return
    return data?.data;
  };

  if (gapMiniAppSdk && gapMiniAppSdk.detectPlatform()) {
    userAgent = gapMiniAppSdk.detectPlatform();
    gapMiniAppSdk.setPlatformExecutor(userAgent, getTransactionStatusHandler);
  }

  const InitGapMiniAppSDK = async () => {
    if (!userAgent || !gapMiniAppSdk) {
      return undefined;
    }

    try {
      const response = await gapMiniAppSdk.getCardData(inputCardData);

      if (response && typeof response === 'object') {
        const convert = JSON.parse(
          JSON.stringify(response),
        ) as TResponseMagtekDevice;
        setDeviceInfo(convert.data);

        return convert.data;
      }
      return undefined;
    } catch (_) {
      return undefined;
    }
  };

  const onCancel = async () => {
    if (deviceInfo?.status === MagtekStatusDevice.active && gapMiniAppSdk) {
      // Read card data on Magtek:
      const cancelCardData: GetCardData = {
        type: 'MAGTEK',
        method: MethodMagTek.CANCEL_EVM_TRANSACTION,
      };
      gapMiniAppSdk?.getCardData(cancelCardData);
    }
  };

  const onCheckStatusDevice = async () => {
    const resp = await InitGapMiniAppSDK();
    return resp?.status ?? MagtekStatusDevice.inactive;
  };

  const onInitPaymentCard = () => {
    setPaymentStatus(undefined);
    // Read card data on Magtek:
    const readCardData: GetCardData = {
      type: 'MAGTEK',
      method: MethodMagTek.START_EVM_TRANSACTION,
      timeOut: 60,
    };
    return gapMiniAppSdk?.getCardData(readCardData);
  };

  useEffect(() => {
    InitGapMiniAppSDK();
  }, []);

  return (
    <GapMagtekSdkContext.Provider
      value={{
        paymentStatus: paymentStatus,
        deviceInfo: deviceInfo,
        event: {
          onCancel: () => onCancel(),
          onCheckStatusDevice: () => onCheckStatusDevice(),
          onInitPaymentCard: () => onInitPaymentCard(),
        },
      }}
    >
      {props.children}
    </GapMagtekSdkContext.Provider>
  );
}
